import { Box } from "@mui/material";
import type { JSX } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { PagePermissionGuard } from "@crm-src/components/page-permission-guard";
import { PageHeader } from "@/components/ui/page-header";

import { PRICING_TITLE } from "./title";
import { ProductsPanel } from "./products/ProductsPanel";

/**
 * CRM ▸ **Pricing** (`/crm/pricing`) — what XOP sells and what it costs, in
 * ONE list.
 *
 * It used to be "Products & Pricing", two tabs over two lists: a catalogue of
 * products → features → versions, and a separate price book of part numbers
 * and prices. The same Value Added Service therefore existed twice, and every
 * quote began by choosing which of the two lists to pick it from. On
 * 2026-09-13 the price moved onto the feature — so the catalogue IS the price
 * book, the page needs no tabs, and a quote line has one source.
 *
 * Two levels, and there is no third: the version level under a feature went in
 * the same change (it priced nothing and named a release that CRM ▸ Releases &
 * Licences already owns).
 *
 * The page sits behind ONE grant, `crm:products_pricing`
 * (→ `products_pricing:access`), super admins only by default — the grant
 * exists so an admin can open the page to sales later by ticking a box in the
 * roles matrix. Visibility is all it confers: setting a price or curating the
 * catalogue needs `admin:users` (the panel hides its write controls with
 * `useCan`, and the BFF refuses regardless).
 */
export default function PricingPage(): JSX.Element {
  const [searchParams] = useSearchParams();

  // The Download report was a tab of the old page until 2026-09-12. It is the
  // download journal on each version now (CRM ▸ Releases, its own entry since
  // 2026-09-13), so an old link goes to the releases those versions belong to
  // rather than landing here silently.
  if (searchParams.get("tab") === "downloads") {
    return <Navigate to="/crm/releases" replace />;
  }

  return (
    <PagePermissionGuard requiredPermission="products_pricing:access">
      <Box>
        {/* Title only — the explanatory paragraph that used to sit here
            (2026-09-13) said nothing the list below does not, and pushed the
            whole page down. What the page is lives in this file's header
            comment and docs/pricing.md, not on the screen. */}
        <PageHeader title={PRICING_TITLE} />
        <Box sx={{ mt: 3 }}>
          <ProductsPanel />
        </Box>
      </Box>
    </PagePermissionGuard>
  );
}
